/** Metadatos mínimos de un subcampo (p. ej. dentro de agrupación tipo 48). */
export type CustomfieldSubMeta = {
  type?: number | string | null;
  format?: boolean | string | number | null;
  regularexpression?: string | null;
  name?: string | null;
  alias?: string | null;
};

export function resolveSubfieldMeta(
  fieldTypesMap: Record<string | number, unknown> | undefined,
  fieldId: string | number
): CustomfieldSubMeta | null {
  if (!fieldTypesMap) {
    return null;
  }
  const entry =
    fieldTypesMap[fieldId] ??
    fieldTypesMap[String(fieldId)] ??
    fieldTypesMap[Number(fieldId)];
  if (entry == null) {
    return null;
  }
  if (typeof entry === 'number') {
    return { type: entry };
  }
  if (typeof entry === 'object') {
    return entry as CustomfieldSubMeta;
  }
  return null;
}

/** Monto CLP: tipo 2 con format y regex (no confundir con correo tipo 2 + regex de email). */
export function isFormattedMontoSubfield(subField: CustomfieldSubMeta | null | undefined): boolean {
  if (!subField || Number(subField.type) !== 2) {
    return false;
  }
  const formatOn =
    subField.format === true ||
    String(subField.format) === 'true' ||
    Number(subField.format) === 1;
  if (!formatOn) {
    return false;
  }
  const re = String(subField.regularexpression ?? '').trim();
  return re !== '';
}

/** Tipo 15 o texto (tipo 2) con regex que incluye @ (Correo Banco BF en Banpro PE). */
export function isEmailLikeSubfield(subField: CustomfieldSubMeta | null | undefined): boolean {
  if (!subField) {
    return false;
  }
  if (Number(subField.type) === 15) {
    return true;
  }
  if (Number(subField.type) !== 2) {
    return false;
  }
  if (String(subField.regularexpression ?? '').includes('@')) {
    return true;
  }
  const label = `${subField.name ?? ''} ${subField.alias ?? ''}`.toLowerCase();
  return /correo|e-mail|email/.test(label);
}

/** Mapa id → metadatos de subcampos para extractData / guardado tipo 48. */
export function buildType48SubfieldMetaMap(
  type48Parents: Array<{ customfields?: Array<Record<string, unknown>> | null }> | null | undefined
): Record<string | number, CustomfieldSubMeta> {
  const map: Record<string | number, CustomfieldSubMeta> = {};
  if (!Array.isArray(type48Parents)) {
    return map;
  }
  for (const parent of type48Parents) {
    for (const nested of parent?.customfields || []) {
      if (nested?.id == null) {
        continue;
      }
      map[nested.id as number] = {
        type: nested.type as CustomfieldSubMeta['type'],
        format: nested.format as CustomfieldSubMeta['format'],
        regularexpression: nested.regularexpression as string | null,
        name: nested.name as string | null,
        alias: nested.alias as string | null,
      };
    }
  }
  return map;
}

/** Campos cuyo valor no debe perder puntos al normalizar JSON de tipo 48. */
export function shouldPreserveDotsForSubfield(subField: CustomfieldSubMeta | null | undefined): boolean {
  if (!subField) {
    return false;
  }
  const type = Number(subField.type);
  if (type === 15 || type === 8 || type === 5) {
    return true;
  }
  return isEmailLikeSubfield(subField);
}

/**
 * true si el array es anidado (tipo 14/55: [{ seleccion, sub }]), no un escalar envuelto ['texto'].
 */
export function isType48NestedJsonArray(value: unknown): boolean {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  const first = value[0];
  if (first == null || typeof first !== 'object' || Array.isArray(first)) {
    return false;
  }
  return 'seleccion' in (first as object) || 'sub' in (first as object) || 'values' in (first as object);
}

/**
 * Extrae texto a sanitizar: solo string o ['string'] (legacy extractData).
 * Números, booleanos y objetos se dejan fuera (null → sin cambio).
 */
export function extractType48ScalarRaw(fieldValue: unknown): string | null {
  if (fieldValue == null) {
    return null;
  }
  if (isType48NestedJsonArray(fieldValue)) {
    return null;
  }
  if (Array.isArray(fieldValue)) {
    if (fieldValue.length === 0) {
      return null;
    }
    const first = fieldValue[0];
    if (typeof first === 'string') {
      return first;
    }
    return null;
  }
  if (typeof fieldValue === 'string') {
    return fieldValue;
  }
  return null;
}

/** Sanitiza subcampo tipo 48 (string o ['string']); deja intactos arrays anidados (tipo 14). */
export function sanitizeType48SubfieldValue(
  fieldValue: unknown,
  subField: CustomfieldSubMeta | null | undefined
): unknown {
  const scalar = extractType48ScalarRaw(fieldValue);
  if (scalar === null) {
    return fieldValue;
  }
  return sanitizeType48ScalarString(scalar, subField);
}

/** Limpia símbolos de monto/formulario; respeta puntos en emails y RUT. */
export function sanitizeType48ScalarString(
  raw: unknown,
  subField: CustomfieldSubMeta | null | undefined
): string {
  const base = String(raw ?? '')
    .replace(/\$/g, '')
    .replace(/%/g, '')
    .trim();
  if (isFormattedMontoSubfield(subField)) {
    return base.replace(/\./g, '').replace(/,/g, '').trim();
  }
  if (shouldPreserveDotsForSubfield(subField)) {
    return base;
  }
  // Valores con @ son correo aunque falte metadata del subcampo (p. ej. getFieldDefById incompleto).
  if (base.includes('@')) {
    return base;
  }
  return base.replace(/\./g, '').trim();
}
